import { createApi } from '@reduxjs/toolkit/query/react';

import { baseQueryWithErrorHandling } from './baseQuery';
import { enhancedApi as taskActivityApi } from './generated/taskActivityService';
import { tasksApiClient } from './tasksService';

export type VoiceAiDashboardCategory =
  | 'Legal Ops Interruption'
  | 'BPO Retrying'
  | 'Provider Working On It'
  | 'Uncategorized';

export type UpdateSource = 'Voice AI' | 'BPO' | 'Upwork';

export type VoiceAiDashboardRow = {
  voice_ai_agent_run_id: string;
  reviewed: boolean;
  source: UpdateSource;
  row_id: string;
  task_update_id: string;
  update_text: string;
  outcome: string;
  matter_name: string;
  matter_id: string;
  task_name: string;
  task_id: string;
  request_type: string;
  provider_name: string;
  created_at: string;
  category: VoiceAiDashboardCategory;
  sort_order: number;
  next_action_raw: string;
  next_action_display: string;
  summary: string;
  due_date: string | null;
  task_url: string;
  assignee_name: string;
  assignee_id: string;
  provider_id: string;
  treatment_record_request_id: string;
  new_phone: string;
  current_channel_type: string;
  current_channel_value: string;
  alternate_handle_type: string;
  alternate_value: string;
};

export type VoiceAiDashboardAssignee = {
  id: string;
  name: string;
};

export type VoiceAiDashboardResponse = {
  rows: VoiceAiDashboardRow[];
  assignees: VoiceAiDashboardAssignee[];
  count: number;
};

type VoiceAiDashboardFilters = {
  since_date?: string; // YYYY-MM-DD
  until_date?: string; // YYYY-MM-DD
  assignee_id?: string;
  category?: VoiceAiDashboardCategory;
  reviewed?: string; // "true" | "false" | "all"
  limit?: number;
  offset?: number;
  ordering?: string;
  source?: UpdateSource;
};

export const voiceAiDashboardApiClient = createApi({
  reducerPath: 'voiceAiDashboardApi',
  baseQuery: baseQueryWithErrorHandling,
  tagTypes: ['VoiceAiDashboard'],
  endpoints: (builder) => ({
    getVoiceAiDashboard: builder.query<
      VoiceAiDashboardResponse,
      VoiceAiDashboardFilters | void
    >({
      query: (params) => {
        const searchParams = new URLSearchParams();
        if (params) {
          if (params.since_date)
            searchParams.set('since_date', params.since_date);
          if (params.until_date)
            searchParams.set('until_date', params.until_date);
          if (params.assignee_id)
            searchParams.set('assignee_id', params.assignee_id);
          if (params.category) searchParams.set('category', params.category);
          if (params.reviewed !== undefined)
            searchParams.set('reviewed', params.reviewed);
          if (params.limit !== undefined)
            searchParams.set('limit', String(params.limit));
          if (params.offset !== undefined)
            searchParams.set('offset', String(params.offset));
          if (params.ordering) searchParams.set('ordering', params.ordering);
          if (params.source) searchParams.set('source', params.source);
        }
        const qs = searchParams.toString();
        return `api/voice-ai-dashboard/${qs ? `?${qs}` : ''}`;
      },
      providesTags: ['VoiceAiDashboard'],
      keepUnusedDataFor: 30,
    }),

    toggleReviewed: builder.mutation<
      { id: string; reviewed: boolean },
      { runId: string; reviewed: boolean }
    >({
      query: ({ runId, reviewed }) => ({
        url: `api/voice-ai-dashboard/${runId}/reviewed/`,
        method: 'PATCH',
        body: { reviewed },
      }),
      invalidatesTags: ['VoiceAiDashboard'],
    }),

    toggleTaskUpdateReviewed: builder.mutation<
      { id: string; reviewed: boolean },
      { taskUpdateId: string; reviewed: boolean }
    >({
      query: ({ taskUpdateId, reviewed }) => ({
        url: `api/voice-ai-dashboard/${taskUpdateId}/task-update-reviewed/`,
        method: 'PATCH',
        body: { reviewed },
      }),
      invalidatesTags: ['VoiceAiDashboard'],
    }),

    updateDueDate: builder.mutation<
      { id: string; due_date: string },
      { runId: string; due_date: string; change_reason: string }
    >({
      query: ({ runId, due_date, change_reason }) => ({
        url: `api/voice-ai-dashboard/${runId}/due-date/`,
        method: 'PATCH',
        body: { due_date, change_reason },
      }),
      invalidatesTags: ['VoiceAiDashboard'],
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        await queryFulfilled;
        // Invalidate task updates and activity caches so the task timeline
        // reflects the due date change and comment (same as tasksService.updateTask)
        dispatch(tasksApiClient.util.invalidateTags(['Tasks', 'TaskUpdates']));
        dispatch(taskActivityApi.util.invalidateTags(['tasks']));
      },
    }),

    updateDueDateByTask: builder.mutation<
      { task_id: string; due_date: string },
      { taskId: string; due_date: string; change_reason: string }
    >({
      query: ({ taskId, due_date, change_reason }) => ({
        url: `api/voice-ai-dashboard/${taskId}/task-due-date/`,
        method: 'PATCH',
        body: { due_date, change_reason },
      }),
      invalidatesTags: ['VoiceAiDashboard'],
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        await queryFulfilled;
        dispatch(tasksApiClient.util.invalidateTags(['Tasks', 'TaskUpdates']));
        dispatch(taskActivityApi.util.invalidateTags(['tasks']));
      },
    }),

    acceptAlternateContact: builder.mutation<
      unknown,
      {
        matterId: string;
        providerId: string;
        requestId: string;
        handle_type: string;
        value: string;
      }
    >({
      query: ({ matterId, providerId, requestId, handle_type, value }) => ({
        url: `api/matters/${matterId}/providers/${providerId}/record-requests/${requestId}/accept-alternate/`,
        method: 'POST',
        body: { handle_type, value },
      }),
      invalidatesTags: ['VoiceAiDashboard'],
    }),
  }),
});

export const {
  useGetVoiceAiDashboardQuery,
  useToggleReviewedMutation,
  useToggleTaskUpdateReviewedMutation,
  useUpdateDueDateMutation,
  useUpdateDueDateByTaskMutation,
  useAcceptAlternateContactMutation,
} = voiceAiDashboardApiClient;
